import type { Market } from "./types";

/**
 * One event's markets, from Digitain's own /Common/GetEvent.
 *
 * ⛔ WHY THIS EXISTS. Every board we post carries ONLY market ids 1 and 702 —
 * both fan-outs ask for `stakeTypes: [1, 702]`, and the 177 other markets in
 * market_catalog_v2 are known only from settled bets. Totals is the biggest
 * market in the book (497,363 against the moneyline's 131,078 once an acca's
 * stake is split across its legs, and 10.9x on singles) and we could not name a
 * single one of its lines.
 *
 * Widening the board was the obvious fix and the wrong one: /suggestions is the
 * pilot's capacity constraint. This fetches ONE event, only for events we
 * actually show.
 *
 * ⚠️ SIZES. The only sizes anyone can check from this repo are the two
 * committed fixtures, and both are TRIMMED captures — they cannot size a real
 * response. Measured 2026-08-31 over `src/testdata/`:
 *
 *   get-event-full-totals-ladder.json  1,729 B raw → 20 lines → 2,496 B normalised
 *   get-event-live-football.json       1,202 B raw →  8 lines → 1,000 B normalised
 *
 * A live match carries ~103 market groups, so the raw response is far larger
 * than either fixture and posting it onward is out of the question. ⛔ But the
 * "~306 KB" and "~900 KB" figures that used to sit in these comments are NOT
 * reproducible from anything in the repo — do not quote them, and do not quote
 * a TRANSFER size at all: the response arrives through the operator's iframe
 * postMessage proxy, so no resource timing is visible from the page.
 *
 * Only the normalised result below is ever posted onward.
 */

export interface MarketLine {
  market_id: number;
  market_name: string;
  selection: string;
  line: number | null;
  stake_id: string;
  odds: number;
  code: number | null;
}

/** ⛔ NO MARKET ALLOW-LIST. Owner's ruling 2026-09-08: "we never looked at other
 *  lines available and tried to sell moneyline — the restriction is self
 *  imposed." Every group the board prices is posted; the backend's line_chooser
 *  decides what it can score and records the rest as `unscored_markets`. The
 *  only bound is size: a live match carries ~103 groups, and the lines nearest
 *  even money are the ones a chooser can sell, so those are kept first. */
export const MAX_LINES_PER_EVENT = 80;
/** The market ids line_chooser scores (FAMILIES in line_chooser.py): handicap,
 *  totals, BTTS, double chance, team totals. Kept ahead of everything else. */
export const SCORED_IDS = new Set([2, 3, 26, 37, 69, 75]);

/** Line groups kept per scored market from the LIVE feed: the argument nearest
 *  even money and the one above it. Over-totals are what this book bets (spec
 *  2026-09-16 §1: Over 0.5–3.5 ≈ 35% of whale stake), and the line above the
 *  main one is where "one more goal" sits. */
export const LIVE_LINE_GROUPS = 2;

/** The scored families' lines out of an already-parsed live-feed event.
 *
 * ⛔ WHY. `mapApiEvent` trims the live feed to 1/702 to keep /suggestions small
 * (`MATCH_RESULT_MARKET_IDS`), which threw away every totals line the browser
 * already held — so a card was built on a moneyline while Over 2.5 sat in memory.
 * This keeps a small, chooser-usable slice instead. Measured body today (box,
 * 2026-09-16): p50 82 KB, max 202 KB — the caller also caps the EVENTS. */
export function scoredMainLines(markets: Market[] | undefined,
                                groupsPerMarket: number = LIVE_LINE_GROUPS): MarketLine[] {
  const out: MarketLine[] = [];
  for (const m of markets || []) {
    const mid = Number(m?.id);
    if (!Number.isFinite(mid) || !SCORED_IDS.has(mid)) continue;
    const sels = (m.selections || []).filter((s) => s && s.stake_id && Number(s.odds) > 1);
    if (!sels.length) continue;
    const toLine = (s: (typeof sels)[number]): MarketLine => ({
      market_id: mid, market_name: String(m.name ?? ""), selection: String(s.name ?? ""),
      line: s.argument == null ? null : Number(s.argument), stake_id: String(s.stake_id),
      odds: Math.round(Number(s.odds) * 100) / 100, code: s.code ?? null,
    });
    const withArg = sels.filter((s) => s.argument != null);
    if (!withArg.length) { out.push(...sels.map(toLine)); continue; }
    const groups = new Map<number, typeof sels>();
    for (const s of withArg) {
      const a = Number(s.argument);
      if (!Number.isFinite(a)) continue;
      (groups.get(a) || groups.set(a, []).get(a)!).push(s);
    }
    const args = [...groups.keys()].sort((a, b) => a - b);
    if (!args.length) continue;
    const unevenness = (a: number): number => {
      const g = groups.get(a)!;
      return g.reduce((t, s) => t + Math.abs(Math.log(Number(s.odds) / 1.9)), 0) / g.length;
    };
    let best = 0;
    for (let i = 1; i < args.length; i++) if (unevenness(args[i]) < unevenness(args[best])) best = i;
    for (const a of args.slice(best, best + groupsPerMarket)) out.push(...groups.get(a)!.map(toLine));
  }
  return out;
}

function num(v: unknown): number | null {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export function normaliseEvent(res: unknown): MarketLine[] {
  const ev = Array.isArray(res) ? res[0] : res;
  if (!ev || typeof ev !== "object") return [];
  const groups = (ev as Record<string, unknown>).StakeTypes;
  if (!Array.isArray(groups)) return [];

  const out: MarketLine[] = [];
  for (const g of groups) {
    if (!g || typeof g !== "object") continue;
    const gg = g as Record<string, unknown>;
    const rawId = num(gg.Id);
    if (rawId === null) continue;
    // ⛔ THE SIGNED HALVES. GetEvent splits one totals ladder across TWO groups:
    // -3 holds the MAIN line alone (2 stakes at total 2.0), 3 holds every other
    // line (0.5 … 4.5). abs() merges them, and it is REQUIRED — reading one
    // sign leaves the picker almost nothing to choose from, silently.
    // market_catalog_v2 and every settled leg use the positive id.
    const marketId = Math.abs(rawId);
    const stakes = gg.Stakes;
    if (!Array.isArray(stakes)) continue;
    for (const s of stakes) {
      if (!s || typeof s !== "object") continue;
      const ss = s as Record<string, unknown>;
      const odds = num(ss.F);
      // An unpriced stake is not a bet. Same rule the moneyline path uses.
      if (odds === null || odds <= 1) continue;
      if (ss.Id == null) continue;
      out.push({
        market_id: marketId,
        market_name: String(gg.N ?? ""),
        selection: String(ss.N ?? ss.SN ?? ""),
        line: num(ss.A),
        stake_id: String(ss.Id),
        odds: Math.round(odds * 100) / 100,
        code: num(ss.SC),
      });
    }
  }
  if (out.length > MAX_LINES_PER_EVENT) {
    // Keep the lines a chooser can sell: nearest to even money first. A 1.03
    // or a 15.0 is refused by the odds band downstream anyway.
    // ⛔ THE MATCH RESULT ALWAYS SURVIVES. parikara 2026-09-09: a pre-match
    // GetEvent carries hundreds of groups, and the 1X2 at 2.25/3.5/3.25 lost to
    // eighty lines priced ~1.9 — so the open fixture's card had no price. It is
    // the market the card is BUILT from; it is kept first, the rest ranked.
    // ⛔ AND EVERY MARKET THE CHOOSER SCORES (2026-09-10, Fenerbahçe–Roma): a
    // 1,076-stake pre-match book filled the eighty with minute-of-goal lines
    // priced ~1.9 and dropped totals (3), BTTS (26) and double chance (37)
    // entirely — the server had facts for over 2.5 and BTTS and no line to put
    // them on. Scored families first (evenness-ranked among themselves), then
    // the rest by evenness; the 1X2 is ahead of both.
    const evenness = (l: MarketLine): number => Math.abs(Math.log(l.odds / 1.9));
    const result = out.filter((l) => MATCH_RESULT_IDS.has(l.market_id));
    const scored = out.filter((l) => !MATCH_RESULT_IDS.has(l.market_id) && SCORED_IDS.has(l.market_id));
    scored.sort((a, b) => evenness(a) - evenness(b));
    const rest = out.filter((l) => !MATCH_RESULT_IDS.has(l.market_id) && !SCORED_IDS.has(l.market_id));
    rest.sort((a, b) => evenness(a) - evenness(b));
    return result.concat(scored, rest).slice(0, MAX_LINES_PER_EVENT);
  }
  return out;
}

/** Match-result market ids, the ones a moneyline card is built from. */
const MATCH_RESULT_IDS = new Set([1, 702]);

/** One event's MATCH-RESULT market, in the shape the board uses.
 *
 * ⛔ WHY THIS EXISTS. Measured live 2026-08-31 on the Lecce v Roma page: the
 * live board entry for the fixture the visitor has OPEN arrives with
 * `markets: []`, while the operator's own page shows a full book. With no
 * priced selection the backend cannot build a card, so the open match — even
 * once it is correctly detected and boosted — loses to any fixture that has
 * prices. Proven server-side: the identical request with a match-result market
 * present returns the open match ranked FIRST.
 *
 * `GetEvent` already carries this; `normaliseEvent` was simply discarding
 * everything that was not a total.
 */
export interface MatchResultMarket {
  id: string;
  name: string;
  selections: Array<{ name: string; odds: number; stake_id?: string }>;
}

export function normaliseMatchResult(res: unknown): MatchResultMarket[] {
  const ev = Array.isArray(res) ? (res as unknown[])[0] : res;
  if (!ev || typeof ev !== "object") return [];
  const groups = (ev as { StakeTypes?: unknown }).StakeTypes;
  if (!Array.isArray(groups)) return [];
  for (const g of groups) {
    if (!g || typeof g !== "object") continue;
    const gg = g as { Id?: unknown; N?: unknown; Stakes?: unknown };
    const raw = num(gg.Id);
    if (raw === null) continue;
    if (!MATCH_RESULT_IDS.has(Math.abs(raw))) continue;
    const stakes = gg.Stakes;
    if (!Array.isArray(stakes)) continue;
    const selections: Array<{ name: string; odds: number; stake_id?: string }> = [];
    for (const s of stakes) {
      if (!s || typeof s !== "object") continue;
      const ss = s as { N?: unknown; F?: unknown; Id?: unknown };
      const odds = num(ss.F);
      if (odds === null || odds <= 1) continue;
      const name = typeof ss.N === "string" ? ss.N : "";
      if (!name) continue;
      selections.push({
        name,
        odds,
        stake_id: ss.Id === null || ss.Id === undefined ? undefined : String(ss.Id),
      });
    }
    if (selections.length >= 2) {
      return [{
        id: String(Math.abs(raw)),
        name: typeof gg.N === "string" ? gg.N : "",
        selections,
      }];
    }
  }
  return [];
}
